import { useCallback } from "react";
import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import {
  getPlayerAge,
  getPlayerArchetype,
  getPlayerContractMonths,
  getPlayerTeamName,
} from "@/features/player-kanban/utils/manualPlayerUtils";

import SidebarPlayerCard from "@/components/organisms/custom/ui/SidebarPlayerCard";
import { Box } from "@/components/atoms/Box";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import type { PlayerKanbanPlayerFragmentFragment } from "@/generated/graphql";
import { formatGeminiValuationRange } from "@/utils/formatCurrencyShort";
import { ifWeb } from "@/utils/platform";

import { WatchlistPlayerCard } from "../../molecules/AddPlayersRow";

/** Pixels from the bottom of the scroll to trigger loading more */
const SCROLL_THRESHOLD = 200;

/**
 * Which surface is rendering the list.
 *
 * The two surfaces show the SAME pool with the same drag behaviour and differ
 * only in row chrome and insets, so they are one component with a closed
 * variant rather than two lists that would drift apart:
 *
 * - `sidebar` — the legacy `PlayerSidebar` rail. Detailed rows (valuation
 *   range, contract, GPR as text) inside the rail's own 24px gutter.
 * - `drawer` — the SE-6554 add-players drawer. Compact `Player Card v7`
 *   watchlist cards, and NO gutter: the drawer shell already insets its content
 *   by 20px, and a second inset here stacked on top of it was what pulled the
 *   cards away from the drawer edges.
 */
export type PlayerListSectionVariant = "sidebar" | "drawer";

/** Per-variant classes for the scroll content column. */
const CONTENT_CLASS: Record<PlayerListSectionVariant, string> = {
  sidebar: "gap-3 px-6 pb-6",
  drawer: "gap-3 pb-6",
};

/**
 * Per-variant classes for the scroller itself.
 *
 * The drawer hides its scrollbar. On web the native track is laid out INSIDE
 * the scroller, so it takes a strip off the card width and reads as extra
 * right-hand margin — costly in a 320px content column, which is why the
 * drawer suppresses it and the far wider legacy rail does not. Scrolling is
 * unaffected in both. `scrollbar-hide` lives in global.css and is the repo's
 * existing answer here; `showsVerticalScrollIndicator` (set below) covers
 * native, where there is no persistent track to reclaim anyway.
 */
const SCROLLER_CLASS: Record<PlayerListSectionVariant, string> = {
  sidebar: "flex-1",
  drawer: `flex-1 ${ifWeb("scrollbar-hide", "")}`.trim(),
};

/**
 * Props for PlayerListSection component.
 */
interface PlayerListSectionV2Props {
  /**
   * Array of player objects to display in the list
   */
  players: PlayerKanbanPlayerFragmentFragment[];
  /**
   * Record of player IDs to their current kanban phase information
   */
  playersOnKanban: Record<string, { phaseId: string; phaseName: string }>;
  /**
   * Whether any item is currently being dragged globally
   */
  isGloballyDragging?: boolean;
  /**
   * Callback invoked when a player card is clicked
   */
  onPlayerClick: (playerId: string) => void;
  /**
   * Callback invoked when a drag operation starts on any player card
   */
  onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  /**
   * Callback invoked when a drag operation ends
   */
  onGlobalDragEnd?: () => void;
  /**
   * Callback invoked to trigger automatic scrolling during drag operations
   */
  onAutoScroll?: (dragX: number) => void;
  /**
   * Callback invoked when the user scrolls near the bottom to load more players
   */
  onLoadMore?: () => void;
  /**
   * Which surface is rendering the list. Defaults to `"sidebar"` — the legacy
   * rail, whose rows and insets are unchanged by SE-6554.
   */
  variant?: PlayerListSectionVariant;
  /**
   * SE-6952: which pool the drawer variant is rendering, driving the card's
   * meta line (watchlist: club · age · contract; roster: age · squad status).
   * Ignored by the sidebar variant.
   */
  metaVariant?: "watchlist" | "roster";
  /**
   * SE-6951: per-card one-click add. Drawer variant only — when absent the
   * cards carry no action row, which is the legacy sidebar's behaviour.
   */
  onAddPlayer?: (playerId: string) => void;
  /** SE-6951: player ids with an add in flight. */
  addingPlayerIds?: readonly string[];
}

/**
 * Container component for displaying a list of players with their kanban status indicators.
 * @param root0 - The component props
 * @param root0.players - Array of player objects to display in the list
 * @param root0.playersOnKanban - Record of player IDs to their current kanban phase information
 * @param root0.isGloballyDragging - Whether any item is currently being dragged globally
 * @param root0.onPlayerClick - Callback invoked when a player card is clicked
 * @param root0.onGlobalDragStart - Callback invoked when a drag operation starts on any player card
 * @param root0.onGlobalDragEnd - Callback invoked when a drag operation ends
 * @param root0.onAutoScroll - Callback invoked to trigger automatic scrolling during drag operations
 * @param root0.onLoadMore - Callback invoked when the user scrolls near the bottom to load more players
 * @param root0.variant - Which surface is rendering the list (SE-6554)
 * @param root0.metaVariant - Which pool the drawer variant renders (SE-6952)
 * @param root0.onAddPlayer - Per-card one-click add, drawer variant only (SE-6951)
 * @param root0.addingPlayerIds - Player ids with an add in flight (SE-6951)
 */
const PlayerListSection = ({
  players,
  playersOnKanban,
  isGloballyDragging: _isGloballyDragging = false,
  onPlayerClick,
  onGlobalDragStart,
  onGlobalDragEnd,
  onAutoScroll,
  onLoadMore,
  variant = "sidebar",
  metaVariant = "watchlist",
  onAddPlayer,
  addingPlayerIds = [],
}: PlayerListSectionV2Props) => {
  const handlePlayerPress = useCallback(
    (playerId: string) => () => {
      onPlayerClick(playerId);
    },
    [onPlayerClick]
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!onLoadMore) return;
      const { layoutMeasurement, contentOffset, contentSize } =
        event.nativeEvent;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < SCROLL_THRESHOLD) {
        onLoadMore();
      }
    },
    [onLoadMore]
  );

  // Show empty state if no players
  if (players.length === 0) {
    return (
      <Box className="flex-1 items-center justify-center p-6">
        <Text variant="unstyled" className="text-center text-content-muted">
          No players found
        </Text>
      </Box>
    );
  }

  return (
    <ScrollView
      className={SCROLLER_CLASS[variant]}
      showsVerticalScrollIndicator={false}
      // These props help with drag overlay visibility
      removeClippedSubviews={false}
      onScroll={handleScroll}
      scrollEventThrottle={400}
    >
      <Stack className={CONTENT_CLASS[variant]}>
        {players.map(player => {
          const kanbanStatus = playersOnKanban[player.id];
          const isOnKanban = Boolean(kanbanStatus);

          if (variant === "drawer") {
            return (
              <WatchlistPlayerCard
                key={player.id}
                player={player}
                isOnKanbanBoard={isOnKanban}
                kanbanPhaseName={kanbanStatus?.phaseName}
                metaVariant={metaVariant}
                isAdding={addingPlayerIds.includes(player.id)}
                onAdd={onAddPlayer}
                onPress={handlePlayerPress(player.id)}
                onDragStart={onGlobalDragStart}
                onDragEnd={onGlobalDragEnd}
                onAutoScroll={onAutoScroll}
              />
            );
          }

          return (
            <Box key={player.id}>
              <SidebarPlayerCard
                playerId={player.id}
                playerName={player?.name ?? ""}
                contractValue={
                  formatGeminiValuationRange(
                    player.bioData?.minGeminiPlayerValuation,
                    player.bioData?.maxGeminiPlayerValuation
                  ) ?? "N/A"
                }
                contractMonths={getPlayerContractMonths(player)}
                teamName={getPlayerTeamName(player)}
                age={getPlayerAge(player)}
                archetype={getPlayerArchetype(player)}
                gpr={Math.round(player.bioData?.gpr || 0)}
                isOnKanbanBoard={isOnKanban}
                kanbanPhaseName={kanbanStatus?.phaseName}
                onPress={handlePlayerPress(player.id)}
                onDragStart={onGlobalDragStart}
                onDragEnd={onGlobalDragEnd}
                onAutoScroll={onAutoScroll}
              />
            </Box>
          );
        })}
      </Stack>
    </ScrollView>
  );
};

export default PlayerListSection;
